import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { skipToken } from '@reduxjs/toolkit/query';

import { captureError } from '@clatter/platform';
import { buttonVariants } from '@clatter/ui';
import { NgBuildIcon, NgButton, NgCard, NgCardContent, NgCardHeader } from '../../UI';
import { config } from '../../../config';
import { useUpload } from '../../../hooks/useUpload';
import { RootState, addNotification, useAppDispatch } from '../../../store';
import { getLongPullUIState } from '../../../store/UI/longPull.slice';
import { foldersService, useGetFolderPathQuery } from '../../../services/folders.service';
import { useLazyGetUploadStatusByIdQuery } from '../../../services/upload.service';
import {
  useGetSlideSimilarityInfoQuery,
  useApplyUploadRequestMutation,
  useCancelUploadRequestMutation,
  SlideSimilarityInfoFromApi,
  SlideApplyAction,
} from '../../../services/slides.service';

import { ClassificationOption, SlideRowDisplayLevel } from '../../../interfaces';
import { useUploadNewAsset } from '../UploadNewAssetContext';
import { getUploadedSlides } from './UploadNewAsset';
import { OriginalSlides } from './OriginalSlides';
import { UploadedSlides } from './UploadedSlides';
import { ResultingSlides, ResultingSlide } from './ResultingSlides';
import { SlideClassification } from './ClassifyDialog';
import { STATUS_TO_CLASSIFICATION } from './SlideLevel';

const API_LEVEL_TO_UI_LEVEL: Record<SlideSimilarityInfoFromApi['similarity_level'], SlideRowDisplayLevel> = {
  identity: SlideRowDisplayLevel.Duplicate,
  new_version: SlideRowDisplayLevel.Similar,
  new: SlideRowDisplayLevel.New,
};

const StyledHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledTitle = styled.h2`
  margin: 0;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.h4};
  color: ${({ theme }) => theme.palette.text};
`;

const StyledSubtitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.palette.textDimmed};
`;

const StyledReviewingLabel = styled.span`
  color: #ff9800;
  font-weight: bold;
`;

const StyledHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledLoadingContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.palette.error};
`;

const SlidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  min-height: 60vh;
  gap: ${({ theme }) => theme.spacing.default};
  padding: ${({ theme }) => theme.spacing.default} 0;
  width: 100%;
`;

export const UploadNewAssetVersion: FC = () => {
  const dispatch = useAppDispatch();
  const { uploadFiles } = useUpload();
  const { pendingVersionUpload, rootFolderId, viewStateKey, closeUploadNewAssetVersion } = useUploadNewAsset();

  const uploadItems = useSelector((state: RootState) => getLongPullUIState(state)?.['global'] || []);

  const [jobId, setJobId] = useState<string | null>(null);
  const [similarityPolling, setSimilarityPolling] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadRequestId, setUploadRequestId] = useState<number | null>(null);
  const [selectedResultSlideId, setSelectedResultSlideId] = useState<number | null>(null);
  const [highlightedOriginalSlideId, setHighlightedOriginalSlideId] = useState<string | null>(null);
  const [classifications, setClassifications] = useState<Record<number, SlideClassification>>({});
  const [isClosing, setIsClosing] = useState(false);

  const [fetchUploadStatus] = useLazyGetUploadStatusByIdQuery();
  const [applyUploadRequest, { isLoading: isApplying }] = useApplyUploadRequestMutation();
  const [cancelUploadRequest, { isLoading: isCancelling }] = useCancelUploadRequestMutation();

  const uploadStatus = useMemo(() => uploadItems.find((item: any) => item.id === jobId), [uploadItems, jobId]);
  const apiStatus = uploadStatus?.apiStatus?.toLowerCase?.() || '';
  const hasError = uploadStatus?.type === 'error' || apiStatus === 'failed' || apiStatus === 'timeout';
  const isUploadIngestCompleted = uploadStatus?.isCompleted || ['success', 'failed', 'timeout'].includes(apiStatus);
  const targetAssetId = pendingVersionUpload?.targetAssetId ?? null;
  const isSimilarityQueryActive = isUploadIngestCompleted && !hasError && !!uploadRequestId && !!targetAssetId;

  const {
    data: similarityResult,
    error: similarityError,
    isError: hasSimilarityError,
    refetch: refetchSimilarityInfo,
  } = useGetSlideSimilarityInfoQuery(
    isSimilarityQueryActive ? { uploadRequestId: uploadRequestId!, assetId: targetAssetId! } : skipToken,
    { pollingInterval: similarityPolling ? config.defaultFetchInterval : 0 },
  );
  const slideSimilarityInfo = similarityResult?.data ?? [];
  const similarityStatus = similarityResult?.status ?? 'processing';

  const slides = useMemo(() => getUploadedSlides(uploadStatus?.responseData), [uploadStatus?.responseData]);

  // @todo: because ingest is returning base64 instead of thumbnail url we need to match slides in uploaded section
  // to show same thumbnails as in the other columns. Remove when ingest will be updated
  const positionsAligned = slideSimilarityInfo.length === 0 || slideSimilarityInfo.length === slides.length;

  const uploadedSlides = useMemo(
    () =>
      slides.map((slide, index) => ({
        ...slide,
        thumbnail_location: positionsAligned ? slideSimilarityInfo[index]?.thumbnail_location : undefined,
      })),
    [slides, slideSimilarityInfo, positionsAligned],
  );

  const resultingSlides: ResultingSlide[] = useMemo(
    () =>
      slideSimilarityInfo.map((info, index) => ({
        id: info.id,
        slide_number: index + 1,
        slide_title: positionsAligned ? slides[index]?.slide_title : undefined,
        thumbnail_location: info.thumbnail_location,
        similarity_level: API_LEVEL_TO_UI_LEVEL[info.similarity_level],
        similar_slides: info.similar_slides,
      })),
    [slideSimilarityInfo, slides, positionsAligned],
  );

  // Uploaded Slides waits for the similarity fetch to actually finish (not just "produced a
  // non-empty result", since a legitimately empty result set would otherwise loop forever).
  const isResultingSlidesReady = hasError || hasSimilarityError || similarityStatus !== 'processing';

  const defaultClassifications = useMemo(
    () =>
      resultingSlides.reduce<Record<number, SlideClassification>>((defaults, slide) => {
        if (
          slide.similarity_level !== SlideRowDisplayLevel.Similar &&
          slide.similarity_level !== SlideRowDisplayLevel.Duplicate
        )
          return defaults;

        const bestMatch = slide.similar_slides[0];
        if (bestMatch?.id) {
          defaults[slide.id] = {
            option: STATUS_TO_CLASSIFICATION[slide.similarity_level],
            replacesSlideId: String(bestMatch.id),
          };
        }

        return defaults;
      }, {}),
    [resultingSlides],
  );

  const effectiveClassifications = useMemo(
    () => ({ ...defaultClassifications, ...classifications }),
    [defaultClassifications, classifications],
  );

  const buildPublishPayload = (): SlideApplyAction[] =>
    resultingSlides.map((slide): SlideApplyAction => {
      const classification = effectiveClassifications[slide.id];
      const asNewSlide: SlideApplyAction = { action: 'new', slide_id: slide.id };

      if (!classification?.replacesSlideId) return asNewSlide;

      const targetSlideId = Number(classification.replacesSlideId);
      if (Number.isNaN(targetSlideId)) return asNewSlide;

      switch (classification.option) {
        case ClassificationOption.VersionUpdate:
          return { action: 'replace', new_slide_id: slide.id, old_slide_id: targetSlideId };
        case ClassificationOption.Duplicate:
          return { action: 'duplicate', remove_slide_id: slide.id, keep_slide_id: targetSlideId };
        default:
          return asNewSlide;
      }
    });

  const handleRetrySimilarity = () => {
    setErrorMessage(null);
    setSimilarityPolling(true);
    refetchSimilarityInfo();
  };

  const handleResultSlideClick = (originalSlideId: string, resultSlideId: number) => {
    if (resultSlideId === selectedResultSlideId) {
      setSelectedResultSlideId(null);
      setHighlightedOriginalSlideId(null);
      return;
    }

    setSelectedResultSlideId(resultSlideId);
    setHighlightedOriginalSlideId(originalSlideId);
  };

  const awaitFolderAssetsRefresh = async () => {
    setIsClosing(true);
    await dispatch(
      foldersService.endpoints.getFolderAssets.initiate({ parentId: pendingVersionUpload!.targetFolderId }),
    )
      .unwrap()
      .catch(() => {});
  };

  const handleUpload = async () => {
    if (!uploadRequestId) {
      closeUploadNewAssetVersion();
      return;
    }

    setErrorMessage(null);
    const actions = buildPublishPayload();
    const applyResult = await applyUploadRequest({ uploadRequestId, actions });

    if ('error' in applyResult) {
      const message = 'Failed to publish this update. Please try again.';
      captureError(applyResult.error, { message, component: 'UploadNewAssetVersion' });
      dispatch(addNotification({ type: 'error', message }));
      setErrorMessage(message);
      return;
    }

    await awaitFolderAssetsRefresh();
    closeUploadNewAssetVersion();
  };

  const handleCancel = async () => {
    if (!uploadRequestId) {
      closeUploadNewAssetVersion();
      return;
    }

    try {
      const statusResult = await fetchUploadStatus({ requestId: uploadRequestId });

      if ('error' in statusResult) {
        const message = 'Failed to look up this upload before cancelling.';
        captureError(statusResult.error, { message, component: 'UploadNewAssetVersion' });
        dispatch(addNotification({ type: 'error', message }));
        return;
      }

      const newAssetVersionId = statusResult.data?.AssetVersionID ?? null;
      if (!newAssetVersionId) return;

      const cancelResult = await cancelUploadRequest({ uploadRequestId, assetVersionId: newAssetVersionId });

      if ('error' in cancelResult && (cancelResult.error as { status?: number })?.status !== 404) {
        const message = 'Failed to cancel this upload.';
        captureError(cancelResult.error, { message, component: 'UploadNewAssetVersion' });
        dispatch(addNotification({ type: 'error', message }));
        return;
      }

      await awaitFolderAssetsRefresh();
    } finally {
      // Cancel always closes the dialog
      closeUploadNewAssetVersion();
    }
  };

  const { data: folderPath } = useGetFolderPathQuery(
    pendingVersionUpload ? { folderId: pendingVersionUpload.targetFolderId } : skipToken,
  );

  const rootIndex = folderPath?.findIndex((folder) => String(folder.id) === String(rootFolderId)) ?? -1;
  const relativeFolderPath = rootIndex !== -1 ? folderPath!.slice(rootIndex + 1) : folderPath;
  const destinationLabel = relativeFolderPath?.map((folder) => folder.name).join(' / ');

  const hasStartedUploadRef = useRef(false);

  useEffect(() => {
    if (!pendingVersionUpload) return;
    if (hasStartedUploadRef.current) return;
    hasStartedUploadRef.current = true;

    uploadFiles({
      file: pendingVersionUpload.file,
      folderId: pendingVersionUpload.targetFolderId,
      asset_id: pendingVersionUpload.targetAssetId,
      isInlineProgress: true,
    }).then((result: any) => {
      if (result?.notificationItemId) setJobId(result.notificationItemId);
      if (result?.uploadRequestId) setUploadRequestId(result.uploadRequestId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingVersionUpload]);

  useEffect(() => {
    if (similarityStatus !== 'processing' || hasSimilarityError) setSimilarityPolling(false);
  }, [similarityStatus, hasSimilarityError]);

  useEffect(() => {
    setSimilarityPolling(true);
  }, [uploadRequestId]);

  useEffect(() => {
    if (!hasSimilarityError) return;

    const message = 'Failed to compare uploaded slides against the existing deck. Please try again.';
    captureError(similarityError, { message, component: 'UploadNewAssetVersion' });
    dispatch(addNotification({ type: 'error', message }));
    setErrorMessage(message);
  }, [hasSimilarityError, similarityError, dispatch]);

  useEffect(() => {
    if (hasError) setErrorMessage(uploadStatus?.message || 'Upload failed. Please try again.');
  }, [hasError, uploadStatus?.message]);

  useEffect(() => {
    if (positionsAligned) return;

    captureError(
      new Error('similarity-info slide count does not match uploaded slide count — positional matching skipped'),
      { component: 'UploadNewAssetVersion', skipConsole: true },
    );
  }, [positionsAligned]);

  if (!pendingVersionUpload) return null;

  return (
    <NgCard>
      <NgCardContent>
        <NgCardHeader hideFilters>
          <StyledHeaderLeft>
            <StyledTitle>New Version of: "{pendingVersionUpload.targetAssetName}"</StyledTitle>
            {errorMessage ? (
              <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
            ) : (
              <StyledSubtitle>
                {destinationLabel && (
                  <>
                    Prospecting <strong>{destinationLabel}</strong> ·{' '}
                    <StyledReviewingLabel>reviewing</StyledReviewingLabel>
                  </>
                )}
              </StyledSubtitle>
            )}
          </StyledHeaderLeft>

          <StyledHeaderRight>
            <NgButton
              variant={buttonVariants.clear}
              disabled={isApplying || (!uploadRequestId && !hasError)}
              isLoading={isCancelling || isClosing}
              onClick={handleCancel}
            >
              Cancel
            </NgButton>

            <NgButton
              variant={buttonVariants.dark}
              disabled={hasError || hasSimilarityError || isCancelling}
              isLoading={!isResultingSlidesReady || isApplying || isClosing}
              loadingSlot={
                <StyledLoadingContent>
                  <NgBuildIcon show />
                  <span>Processing…</span>
                </StyledLoadingContent>
              }
              onClick={handleUpload}
            >
              Publish Update
            </NgButton>
          </StyledHeaderRight>
        </NgCardHeader>

        <SlidesGrid>
          <OriginalSlides
            slides={pendingVersionUpload.targetAssetSlides}
            highlightedSlideId={highlightedOriginalSlideId}
            viewStateKey={viewStateKey}
          />
          <UploadedSlides
            slides={uploadedSlides}
            hasError={hasError}
            viewStateKey={viewStateKey}
            isLoading={!isResultingSlidesReady}
          />
          <ResultingSlides
            slides={resultingSlides}
            originalSlides={pendingVersionUpload.targetAssetSlides}
            classifications={effectiveClassifications}
            selectedSlideId={selectedResultSlideId}
            onSlideClick={handleResultSlideClick}
            onClassify={(newSlideId, classification) =>
              setClassifications((prev) => ({ ...prev, [newSlideId]: classification }))
            }
            hasError={hasError || hasSimilarityError}
            onRetry={handleRetrySimilarity}
            viewStateKey={viewStateKey}
          />
        </SlidesGrid>
      </NgCardContent>
    </NgCard>
  );
};
